import { createFileRoute } from "@tanstack/react-router";
import { IMAGES } from "@/lib/images";
import {
  GuideHero,
  GuideBody,
  GuideFaq,
  GuideCta,
  GuideRelated,
  GuideInlineCta,
  CheckList,
  KeyStats,
  ProsCons,
  StepProcess,
  ResponsiveTable,
  Callout,
  buildGuideHead,
  type GuideMeta,
} from "@/components/GuideLayout";

const meta: GuideMeta = {
  category: "Dekorfilm",
  title: "Dekorfilm & frostat glas — insynsskydd, branding och designval",
  tldr:
    "Dekorfilm är ett samlingsnamn för fönsterfilm som ger frostat eller mönstrat glas utan att man behöver byta själva rutan. Den används för insynsskydd på mötesrum, glasväggar i kontor, butiksskyltar med text och logotyp, samt för att ge gamla glasdörrar och badrumsfönster nytt liv. BBN Nordic installerar dekorfilm från 3M och Solar Screen i hela Skåne och Göteborg.",
  readTimeMin: 7,
  heroImage: IMAGES.dekorfolieHero,
  path: "/guider/dekorfilm-frostat-glas",
  updated: "2026-06-16",
  faqs: [
    {
      q: "Hur länge håller frostfilm på en glasvägg?",
      a: "3M:s dekorfilm har 12+ års förväntad livslängd inomhus utan att gulna eller flagna. BBN Nordic lämnar 10 års produktgaranti och 10 års arbetsgaranti på installationen.",
    },
    {
      q: "Kan jag få frostfilm i delvis mönster — t.ex. en ränder eller logotyp?",
      a: "Ja. Frostfilm kan plottas med valfritt mönster: horisontella ränder för glasväggar, företagslogotyp i frostat utseende, dekorativa mönster för butiker. BBN gör skissförslag innan vi plottar.",
    },
    {
      q: "Är frostfilm samma sak som etsat glas?",
      a: "Visuellt ser de väldigt lika ut, men frostfilm är en självhäftande film som kan tas bort och bytas — etsat glas är permanent. För hyrda lokaler och föränderliga kontorsmiljöer är film alltid att föredra.",
    },
    {
      q: "Kan dekorfilm användas på spegelglas eller färgat glas?",
      a: "Ja, men ytan måste vara plan och fri från textur. Vi gör en kostnadsfri inventering och meddelar om något glas inte är lämpligt.",
    },
    {
      q: "Får dekorfilm finnas på utrymningsvägar och glas­dörrar?",
      a: "Ja — frostfilm med markeringar är vanligt på glasdörrar (och ofta ett krav enligt AFS) eftersom det förhindrar att människor går rakt in i glaset. BBN dimensionerar markeringen enligt gällande regelverk.",
    },
  ],
  related: [
    { to: "/dekorfilm", label: "Dekorfilm — översikt (glasväggar, avskärmning, logotyp, renovering)" },
    { to: "/frostat-glas-vs-dekorfolie", label: "Frostat glas vs dekorfolie — jämförelse" },
  ],
};

export const Route = createFileRoute("/guider/dekorfilm-frostat-glas")({
  head: () => buildGuideHead(meta),
  component: Page,
});

function Page() {
  return (
    <main className="bg-background">
      <GuideHero meta={meta} />
      <GuideBody>
        <KeyStats
          items={[
            { value: "12+ år", label: "Livslängd", sub: "inomhus, utan gulning" },
            { value: "10 år", label: "Produktgaranti", sub: "3M / Solar Screen" },
            { value: "4–8 h", label: "Installation", sub: "typisk kontorsetapp" },
            { value: "0 spår", label: "Tas bort", sub: "perfekt för hyrda lokaler" },
          ]}
        />

        <h2>När frostfilm är bättre än persienner</h2>
        <p>
          Glasade mötesrum, glasväggar mellan kontor och receptioner med stora skyltfönster
          har ofta samma problem: man vill ha ljus, men inte insyn. Persienner blockerar
          både insyn <em>och</em> ljus, behöver underhåll och samlar damm. Frostfilm däremot
          släpper in dagsljuset diffust men gör det omöjligt att se vad som händer på
          andra sidan glaset.
        </p>

        <ProsCons
          prosTitle="Dekorfilm — fördelar"
          consTitle="Dekorfilm — begränsningar"
          pros={[
            "Insynsskydd utan att blockera dagsljus",
            "Kan plottas i valfritt mönster, logotyp eller höjd",
            "Tas bort utan spår — funkar i hyrda lokaler",
            "Uppfyller AFS-krav på markering av glasdörrar",
            "Kostnadsfraktion jämfört med etsat eller bytt glas",
          ]}
          cons={[
            "Ger inte mörkläggning — ljus passerar diffust",
            "Plottad logotyp/mönster är permanent på just den filmen",
            "Kräver plant, slätt glas (ej texturerade rutor)",
          ]}
        />

        <GuideInlineCta />
        <h2>Fem vanliga användningsområden</h2>
        <h3>1. Mötesrum & glasväggar</h3>
        <p>
          Klassiskt kontorsproblem: glasväggar mellan kontor som ger ljus men ingen
          avskärmning. En horisontell frostränder på ögonhöjd är den mest använda lösningen
          — ger insynsskydd där det behövs men släpper igenom ljuset över och under.
        </p>

        <h3>2. Insynsskydd för badrum, omklädningsrum, kontor i markplan</h3>
        <p>
          Heltäckande frostfilm ersätter klassiska "blast"-glas (sandblästrat) på en
          eftermiddag, utan att fönstret behöver bytas. Filmen tas dessutom bort utan spår
          om hyresvärden vill ha glaset i ursprungligt skick vid utflytt.
        </p>

        <h3>3. Logotyp i frostat utseende</h3>
        <p>
          Receptioner, butiksskyltar och konferensrum får företagets logotyp plottad ur
          frostfilmen, vilket ger ett premium-utseende utan kostnaden för etsat glas.
          Logotypen kan kompletteras med solid kulör eller spegel-finish.
        </p>

        <h3>4. Säkerhetsmarkeringar på glasdörrar</h3>
        <p>
          AFS-kraven på arbetsplatser föreskriver tydliga markeringar på glasdörrar
          och glaspartier i passager — dekorfilm är det enklaste sättet att uppfylla
          kravet samtidigt som det ser snyggt ut.
        </p>

        <h3>5. Renovering av gamla glasrutor</h3>
        <p>
          Repor, gulnande filmer från 70-talet, etsskador från diskmedel — dekorfilm
          döljer optiska skavanker till en bråkdel av kostnaden för glasbyte.
        </p>

        <Callout variant="info" title="Standardhöjd för frostränder på glasvägg">
          <p>
            Vanligast är en 80–120 cm hög frostränder med underkant 90 cm från golv. Det ger
            insynsskydd i sittande och stående ögonhöjd men släpper igenom ljus över och under
            ränderna. BBN tar fram skissförslag innan plottning.
          </p>
        </Callout>

        <h2>Vilken frosttyp passar vilket projekt?</h2>
        <ResponsiveTable>
          <thead>
            <tr>
              <th>Typ</th>
              <th>Utseende</th>
              <th>Typisk användning</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Klassisk Frost</td>
              <td>Likformigt mjölkvitt</td>
              <td>Mötesrum, badrum, glasväggar</td>
            </tr>
            <tr>
              <td>Frost med mönster</td>
              <td>Ränder, punkter, gradient</td>
              <td>Receptioner, designkontor</td>
            </tr>
            <tr>
              <td>Plottad logotyp</td>
              <td>Frostat företagsmärke på klart glas</td>
              <td>Skyltfönster, mötesrum, hissar</td>
            </tr>
            <tr>
              <td>Solid kulörfilm</td>
              <td>Helfärgad, ogenomskinlig</td>
              <td>Bakväggar, butiksbranding</td>
            </tr>
            <tr>
              <td>Spegelfilm / one-way</td>
              <td>Speglar utåt, syn inifrån</td>
              <td>Kontor i markplan, butiksskyltar</td>
            </tr>
          </tbody>
        </ResponsiveTable>

        <GuideInlineCta />
        <h2>Process — från idé till plottad film på plats</h2>
        <StepProcess
          steps={[
            { title: "Skicka in skiss & mått", body: "Bifoga logotyp eller önskat mönster + mått per glasparti, så återkommer vi med tekniskt förslag." },
            { title: "Förslagsskiss + offert", body: "Du får proportionell skiss på hur filmen kommer se ut i rummet och fast pris." },
            { title: "Godkännande", body: "Du justerar mönster, höjder och kulör. Inget plottas innan du gett tummen upp." },
            { title: "Plottning i verkstad", body: "Filmen skärs på CAD-styrd plotter — exakt på millimetern, inklusive logotyp och text." },
            { title: "Montering på plats", body: "Auktoriserad tekniker monterar filmen utan damm. En kontorsetapp tar normalt 4–8 timmar." },
          ]}
        />

        <h2>Underhåll</h2>
        <p>
          Frostfilm rengörs med mjuk mikrofiber och vatten — alltså samma som glaset
          tål. Undvik slipmedel, ammoniakbaserade produkter och vassa skrapor; det är
          de enda sätten att skada filmen.
        </p>
      </GuideBody>
      <GuideFaq items={meta.faqs ?? []} />
      <GuideCta />
      <GuideRelated items={meta.related ?? []} />
    </main>
  );
}
